import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "space-y-10" }
const _hoisted_2 = {
  key: 0,
  class: "animate-fade-in"
}
const _hoisted_3 = {
  key: 1,
  class: "animate-fade-in text-center"
}
const _hoisted_4 = {
  key: 2,
  class: "grid place-items-stretch gap-6 md:grid-cols-2 md:gap-10"
}
const _hoisted_5 = {
  key: 3,
  class: "italic text-concrete-700"
}

import { ref, computed, onMounted, onUnmounted } from "vue";
import { Location } from "~/js/interfaces/locations";
import RegionalMap from "~/vue/organisms/maps/RegionalMap.vue";
import CardLocationCompact from "~/vue/molecules/cards/CardLocationCompact.vue";
import { Translations } from "~/js/interfaces/translations";
import { getTranslation, getGlobalTranslation } from "~/js/composables/translations";

interface RegionalLocation extends Location {
	rawZip: number;
	rawState: string | null;
	rawCountry: string | null;
	city: string | null;
}

interface Props {
	locations: RegionalLocation[];
	filterStateCode?: string | null;
	filterText?: string | null;
	filterCountry?: string | null;
	showMap?: boolean;
	utilities?: string;
	translations?: Translations | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GridRegionalLocations',
  props: {
    locations: { default: () => [] },
    filterStateCode: { default: null },
    filterText: { default: null },
    filterCountry: { default: null },
    showMap: { type: Boolean, default: true },
    utilities: { default: "" },
    translations: { default: null }
  },
  setup(__props: any) {

const props = __props;

const activeLocationId = ref<string | null>(null);
const mapRef = ref<InstanceType<typeof RegionalMap> | null>(null);

const filteredLocations = computed(() => {
	if (!props.filterStateCode && !props.filterText) {
		return props.locations;
	}

	return props.locations.filter((loc) => {
		if (props.filterCountry && loc.rawCountry) {
			if (props.filterCountry.toLowerCase() !== loc.rawCountry.toLowerCase()) {
				return false;
			}
		}

		let derivedState = null;
		const zip = loc.rawZip;
		const country = (loc.rawCountry || "").toLowerCase();

		// Had to add this part to bypass mapbox inconsistencies for the States.
		if (["belgium", "belgië", "belgique"].includes(country)) {
			if (zip >= 1000 && zip <= 1299) derivedState = "BRU";
			else if (zip >= 1300 && zip <= 1499) derivedState = "WBR";
			else if ((zip >= 1500 && zip <= 1999) || (zip >= 3000 && zip <= 3499)) derivedState = "VBR";
			else if (zip >= 2000 && zip <= 2999) derivedState = "VAN";
			else if (zip >= 3500 && zip <= 3999) derivedState = "VLI";
			else if (zip >= 4000 && zip <= 4999) derivedState = "WLG";
			else if (zip >= 5000 && zip <= 5999) derivedState = "WNA";
			else if ((zip >= 6000 && zip <= 6599) || (zip >= 7000 && zip <= 7999)) derivedState = "WHT";
			else if (zip >= 6600 && zip <= 6999) derivedState = "WLX";
			else if (zip >= 8000 && zip <= 8999) derivedState = "VWV";
			else if (zip >= 9000 && zip <= 9999) derivedState = "VOV";
		}

		if (props.filterStateCode) {
			return loc.rawState === props.filterStateCode || derivedState === props.filterStateCode;
		} else if (props.filterText) {
			const term = props.filterText.toLowerCase();
			const city = (loc.city || "").toLowerCase();
			const name = (loc.title || "").toLowerCase();
			return city === term || name === term;
		}

		return false;
	});
});

const gridItems = computed(() => {
	// If a marker is selected, show only that one.
	if (activeLocationId.value) {
		return filteredLocations.value.filter((loc) => loc.id === activeLocationId.value);
	}
	// Otherwise show the full filtered list
	return filteredLocations.value;
});

// Handle map marker click
const handleMapFilter = (id: string) => {
	activeLocationId.value = id;
};

const resetFilter = () => {
	activeLocationId.value = null;
	// Tell the map to reset its visual state
	if (mapRef.value) {
		mapRef.value.reset();
	}
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.showMap && filteredLocations.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(RegionalMap, {
            ref_key: "mapRef",
            ref: mapRef,
            locations: filteredLocations.value,
            utilities: "shadow-card",
            onFilter: handleMapFilter
          }, null, 8, ["locations"])
        ]))
      : _createCommentVNode("", true),
    (activeLocationId.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("button", {
            class: "rounded-full bg-viridian-500 px-6 py-2 font-bold text-white shadow-md transition hover:bg-viridian-700",
            onClick: resetFilter
          }, _toDisplayString(_unref(getTranslation)(__props.translations || {}, "Show all locations in this region")), 1)
        ]))
      : _createCommentVNode("", true),
    (gridItems.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(gridItems.value, (card) => {
            return (_openBlock(), _createElementBlock("div", {
              key: card.id,
              class: "h-full transition-all duration-500"
            }, [
              _createVNode(CardLocationCompact, {
                location: card,
                utilities: "h-full"
              }, null, 8, ["location"])
            ]))
          }), 128))
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("p", null, [
            _createTextVNode(_toDisplayString(_unref(getTranslation)(__props.translations || {}, "No warehouses found matching")) + ": ", 1),
            _createElementVNode("strong", null, _toDisplayString(__props.filterText || __props.filterStateCode), 1)
          ])
        ]))
  ]))
}
}

})